import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useHasPerm } from "@/components/PermissionGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Package, Pencil, Trash2, Scan, Plus, TrendingUp, TrendingDown } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";
import { toast } from "sonner";

const netFromGross = (gross, iva) => {
  const i = parseFloat(iva) || 0;
  const g = parseFloat(gross) || 0;
  return +(g / (1 + i / 100)).toFixed(4);
};
const grossFromNet = (net, iva) => {
  const i = parseFloat(iva) || 0;
  const n = parseFloat(net) || 0;
  return +(n * (1 + i / 100)).toFixed(4);
};
const numVal = (v) => (v === "" || v === null || v === undefined ? "" : v);

export default function ProdottoDettaglio() {
  const hasPerm = useHasPerm();
  const canEdit = hasPerm("prodotti:edit");
  const canDelete = hasPerm("prodotti:delete");
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [history, setHistory] = useState([]);
  const [priceChanges, setPriceChanges] = useState([]);
  const [movements, setMovements] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [form, setForm] = useState(null);
  // Quick-add supplier
  const [quickSupplierOpen, setQuickSupplierOpen] = useState(false);
  const [quickSupplier, setQuickSupplier] = useState({
    ragione_sociale: "", nome: "", email: "", telefono: "",
  });
  const [quickBusy, setQuickBusy] = useState(false);

  const suppliersSorted = useMemo(
    () => [...suppliers].sort((a, b) => a.ragione_sociale.localeCompare(b.ragione_sociale, "it")),
    [suppliers]
  );
  const depositsSorted = useMemo(
    () => [...deposits].sort((a, b) => a.nome.localeCompare(b.nome, "it")),
    [deposits]
  );

  // Base storage units only — excludes any unit whose name starts with "confez"
  const isConfezioneUnit = (name) => (name || "").trim().toLowerCase().startsWith("confez");
  const baseUnits = useMemo(
    () => units.filter((u) => !isConfezioneUnit(u.nome)),
    [units]
  );

  const loadAll = async () => {
    try {
      const [p, h, m, s, d, c, u, pc] = await Promise.all([
        api.get(`/products/${id}`),
        api.get(`/products/${id}/history`),
        api.get(`/movements?prodotto_id=${id}&limit=500`),
        api.get("/suppliers"),
        api.get("/deposits"),
        api.get("/categories"),
        api.get("/units"),
        api.get(`/products/${id}/price-changes`),
      ]);
      setProduct(p.data);
      setHistory(h.data);
      setMovements(m.data);
      setSuppliers(s.data);
      setPriceChanges(pc.data || []);
      setDeposits(d.data);
      setCategories(c.data);
      setUnits(u.data);
    } catch (e) {
      toast.error("Errore caricamento prodotto");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [id]); // eslint-disable-line

  const supplier = suppliers.find((x) => x.id === product?.fornitore_id) || null;
  const deposit = deposits.find((x) => x.id === product?.deposito_id) || null;
  const category = categories.find((x) => x.id === product?.categoria_id) || null;
  const unit = units.find((x) => x.id === product?.unita_misura_id) || null;

  const openEdit = () => {
    // Normalize legacy pezzi_per_confezione: 0/1 → empty (not orderable / trivial)
    const ppc = product?.pezzi_per_confezione;
    setForm({ ...product, pezzi_per_confezione: ppc && ppc > 1 ? ppc : "" });
    setEditOpen(true);
  };

  const updateForm = (changes) => {
    setForm((prev) => {
      const next = { ...prev, ...changes };
      const pa = parseFloat(next.prezzo_acquisto) || 0;
      const pv = parseFloat(next.prezzo_vendita) || 0;
      if (pa > 0 && pv > 0) next.ricarico = +(((pv - pa) / pa) * 100).toFixed(2);
      else next.ricarico = "";
      return next;
    });
  };

  const onPriceField = (key, value) => {
    const v = parseFloat(value) || 0;
    if (key === "prezzo_acquisto_con_iva") {
      updateForm({ prezzo_acquisto_con_iva: v, prezzo_acquisto: netFromGross(v, form.iva_acquisto) });
    } else if (key === "prezzo_acquisto") {
      updateForm({ prezzo_acquisto: v, prezzo_acquisto_con_iva: grossFromNet(v, form.iva_acquisto) });
    } else if (key === "iva_acquisto") {
      updateForm({ iva_acquisto: v, prezzo_acquisto: netFromGross(form.prezzo_acquisto_con_iva, v) });
    } else if (key === "prezzo_vendita_con_iva") {
      updateForm({ prezzo_vendita_con_iva: v, prezzo_vendita: netFromGross(v, form.iva_vendita) });
    } else if (key === "prezzo_vendita") {
      updateForm({ prezzo_vendita: v, prezzo_vendita_con_iva: grossFromNet(v, form.iva_vendita) });
    } else if (key === "iva_vendita") {
      updateForm({ iva_vendita: v, prezzo_vendita: netFromGross(form.prezzo_vendita_con_iva, v) });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome_articolo || !form.categoria_id || !form.deposito_id || !form.unita_misura_id) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    const selectedUnit = units.find((u) => u.id === form.unita_misura_id);
    if (selectedUnit && isConfezioneUnit(selectedUnit.nome)) {
      toast.error("L'unità di misura base non può essere 'Confezione' (è un moltiplicatore, non un'unità di stoccaggio)");
      return;
    }
    try {
      // Coerce any "" numeric fields to 0 to avoid 422
      const numericKeys = [
        "quantita", "soglia_minima", "prezzo_acquisto", "prezzo_acquisto_con_iva",
        "iva_acquisto", "prezzo_vendita", "prezzo_vendita_con_iva", "iva_vendita",
        "ricarico", "minimo_ordine",
      ];
      const payload = { ...form, fornitore_id: form.fornitore_id || null };
      numericKeys.forEach((k) => {
        const v = payload[k];
        if (v === "" || v === null || v === undefined) payload[k] = 0;
        else payload[k] = parseFloat(v) || 0;
      });
      // pezzi_per_confezione: empty/invalid → 0 (means "not orderable in confezioni")
      const ppcRaw = form.pezzi_per_confezione;
      if (ppcRaw === "" || ppcRaw === null || ppcRaw === undefined) {
        payload.pezzi_per_confezione = 0;
      } else {
        const n = parseInt(ppcRaw);
        payload.pezzi_per_confezione = !isNaN(n) && n > 0 ? n : 0;
      }
      await api.put(`/products/${id}`, payload);
      toast.success("Prodotto aggiornato");
      setEditOpen(false);
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/products/${id}`);
      toast.success("Prodotto eliminato");
      navigate("/prodotti");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const submitQuickSupplier = async (e) => {
    e.preventDefault();
    if (!quickSupplier.ragione_sociale || !quickSupplier.nome || !quickSupplier.email || !quickSupplier.telefono) {
      toast.error("Compila ragione sociale, nome, email e telefono");
      return;
    }
    setQuickBusy(true);
    try {
      const { data } = await api.post("/suppliers", { ...quickSupplier });
      toast.success("Fornitore creato");
      const reload = await api.get("/suppliers");
      setSuppliers(reload.data);
      updateForm({ fornitore_id: data.id });
      setQuickSupplierOpen(false);
      setQuickSupplier({ ragione_sociale: "", nome: "", email: "", telefono: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore creazione");
    } finally {
      setQuickBusy(false);
    }
  };

  if (loading) return <div className="font-brand text-2xl">Caricamento…</div>;
  if (!product) return <div>Prodotto non trovato</div>;

  const chartData = history.map((h, i) => ({
    index: i + 1,
    data: new Date(h.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }),
    Acquisto: h.prezzo_acquisto,
    Vendita: h.prezzo_vendita,
  }));

  return (
    <div className="space-y-6" data-testid="prodotto-dettaglio-page">
      <Link to="/prodotti" className="inline-flex items-center gap-2 text-sm hover:underline" style={{ color: "var(--ds-primary)" }} data-testid="back-prodotti">
        <ArrowLeft size={14} /> Torna ai prodotti
      </Link>

      <div className="flex flex-col md:flex-row md:items-start gap-4 md:justify-between">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "rgba(180,107,82,0.1)", color: "var(--ds-primary)" }}>
            <Package size={28} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="label-tag mb-1">Articolo</div>
            <h1 className="font-brand text-4xl sm:text-5xl font-light tracking-tight">{product.nome_articolo}</h1>
            <div className="text-sm mt-2 font-mono" style={{ color: "var(--ds-text-secondary)" }}>
              {product.codice_barre || "Senza codice"}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button onClick={openEdit} className="ds-btn-primary" style={{ background: "var(--ds-primary)" }} data-testid="dettaglio-edit-button">
              <Pencil size={14} className="mr-1.5" /> Modifica
            </Button>
          )}
          {canDelete && (
            <Button onClick={() => setDeleteOpen(true)} variant="outline" className="rounded-lg" style={{ borderColor: "var(--ds-danger)", color: "var(--ds-danger)" }} data-testid="dettaglio-delete-button">
              <Trash2 size={14} className="mr-1.5" /> Elimina
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {(() => {
          const ricarico = Number(product.ricarico || 0);
          const ricaricoColor = ricarico >= 20
            ? "#7B8B6F"  // verde
            : ricarico >= 10
            ? "#D4A24A"  // giallo/oro
            : "#B85450"; // rosso
          const ricaricoLabel = ricarico >= 20 ? "alto" : ricarico >= 10 ? "medio" : "basso";
          const fields = [
            ["Quantità", `${product.quantita} ${unit?.nome ?? ""}`],
            ["Soglia minima", product.soglia_minima],
            ["Unità misura", unit?.nome || "—"],
            ["Pezzi/Conf.", product.pezzi_per_confezione && product.pezzi_per_confezione > 1 ? product.pezzi_per_confezione : "—"],
            ["Categoria", category?.nome || "—"],
            ["Deposito", deposit?.nome || "—"],
            ["Fornitore", supplier?.ragione_sociale || "—"],
            ["Codice a barre", product.codice_barre || "—"],
            ["Prezzo acquisto (senza IVA)", `€ ${Number(product.prezzo_acquisto || 0).toFixed(2)}`],
            ["IVA acquisto", `${product.iva_acquisto ?? 22}%`],
            ["Prezzo acquisto (con IVA)", `€ ${Number(product.prezzo_acquisto_con_iva || 0).toFixed(2)}`],
            ["Minimo d'ordine", product.minimo_ordine || "—"],
            ["Prezzo vendita (senza IVA)", `€ ${Number(product.prezzo_vendita || 0).toFixed(2)}`],
            ["IVA vendita", `${product.iva_vendita ?? 22}%`],
            ["Prezzo vendita (con IVA)", `€ ${Number(product.prezzo_vendita_con_iva || 0).toFixed(2)}`],
          ];
          return (
            <>
              {fields.map(([k, v]) => (
                <div key={k} className="ds-card p-4">
                  <div className="label-tag">{k}</div>
                  <div className="font-primary text-base font-medium mt-1" style={{ color: "var(--ds-text-primary)" }}>{v}</div>
                </div>
              ))}
              {/* Ricarico con indicatore colorato */}
              <div className="ds-card p-4" data-testid="ricarico-indicator">
                <div className="label-tag flex items-center justify-between">
                  <span>Ricarico</span>
                  <span
                    className="inline-block px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider"
                    style={{ background: `${ricaricoColor}22`, color: ricaricoColor }}
                  >
                    {ricaricoLabel}
                  </span>
                </div>
                <div className="font-primary text-base font-medium mt-1 flex items-center gap-2">
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ background: ricaricoColor }}
                  />
                  <span style={{ color: ricaricoColor }}>{ricarico}%</span>
                </div>
              </div>
            </>
          );
        })()}
      </div>

      <div className="ds-card p-6" data-testid="prodotto-storico">
        <div className="label-tag">Storico</div>
        <h3 className="font-primary text-xl font-medium mt-1 mb-4">Variazioni di prezzo</h3>
        {chartData.length < 2 ? (
          <div className="text-sm py-8 text-center" style={{ color: "var(--ds-text-secondary)" }}>
            Storico insufficiente. I prezzi vengono tracciati ad ogni modifica.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E1D9" vertical={false} />
              <XAxis dataKey="data" tick={{ fill: "#8A7358", fontSize: 12 }} axisLine={{ stroke: "#E8E1D9" }} />
              <YAxis tick={{ fill: "#8A7358", fontSize: 12 }} axisLine={{ stroke: "#E8E1D9" }} />
              <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E8E1D9", borderRadius: 10, color: "#3D2B1F" }} formatter={(v) => `€ ${Number(v).toFixed(2)}`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="Acquisto" stroke="#8B5A2B" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="Vendita" stroke="#B46B52" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Storico variazioni prezzo da fatture */}
      <div className="ds-card p-6" data-testid="prodotto-price-changes">
        <div className="label-tag">Storico</div>
        <h3 className="font-primary text-xl font-medium mt-1 mb-4">Variazioni dalle fatture</h3>
        {priceChanges.length === 0 ? (
          <div className="text-sm py-6 text-center" style={{ color: "var(--ds-text-secondary)" }}>
            Nessuna variazione registrata. Importa fatture XML / P7M nella sezione <Link to="/fatture" className="underline" style={{ color: "var(--ds-primary)" }}>Fatture</Link>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--ds-border)" }}>
                  <th className="text-left py-2 label-tag">Data</th>
                  <th className="text-left py-2 label-tag">Fattura</th>
                  <th className="text-right py-2 label-tag">Vecchio prezzo</th>
                  <th className="text-right py-2 label-tag">Nuovo prezzo</th>
                  <th className="text-right py-2 label-tag">Variazione</th>
                </tr>
              </thead>
              <tbody>
                {priceChanges.map((c) => {
                  const v = Number(c.variazione_perc || 0);
                  const up = v >= 0;
                  return (
                    <tr key={c.id} className="border-b" style={{ borderColor: "var(--ds-border)" }}>
                      <td className="py-2 text-xs">
                        {new Date(c.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="py-2 text-xs">{c.fattura_numero || "—"}</td>
                      <td className="py-2 text-right">€ {Number(c.vecchio_prezzo || 0).toFixed(4)}</td>
                      <td className="py-2 text-right font-medium">€ {Number(c.nuovo_prezzo || 0).toFixed(4)}</td>
                      <td className="py-2 text-right">
                        <span className="inline-flex items-center gap-1 font-medium" style={{ color: up ? "var(--ds-danger)" : "#15803D" }}>
                          {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          {up ? "+" : ""}{v.toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {product.nota && (
        <div className="ds-card p-6">
          <div className="label-tag">Nota</div>
          <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--ds-text-secondary)" }}>{product.nota}</p>
        </div>
      )}

      {/* Storico movimenti */}
      <div className="ds-card p-6">
        <div className="label-tag">Storico</div>
        <h3 className="font-primary text-xl font-medium mt-1">Movimenti di magazzino</h3>
        {movements.length === 0 ? (
          <div className="text-sm py-8 text-center" style={{ color: "var(--ds-text-secondary)" }}>
            Nessun movimento registrato per questo prodotto.
          </div>
        ) : (
          <>
            {/* Aggregato per deposito */}
            {(() => {
              const byDep = {};
              movements.forEach((m) => {
                const dep = m.deposito_id || m.to_deposito_id || "—";
                if (!byDep[dep]) byDep[dep] = { carico: 0, scarico: 0, count: 0 };
                if (m.tipo === "carico") byDep[dep].carico += Number(m.quantita) || 0;
                else byDep[dep].scarico += Number(m.quantita) || 0;
                byDep[dep].count += 1;
              });
              const rows = Object.entries(byDep)
                .map(([depId, agg]) => ({
                  depId,
                  depName: deposits.find((d) => d.id === depId)?.nome || "—",
                  ...agg,
                  net: agg.carico - agg.scarico,
                }))
                .sort((a, b) => a.depName.localeCompare(b.depName, "it"));
              return (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {rows.map((r) => (
                    <div key={r.depId} className="ds-card p-4" style={{ background: "var(--ds-bg)" }}>
                      <div className="label-tag">{r.depName}</div>
                      <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--ds-text-secondary)" }}>Entrate</div>
                          <div className="font-medium" style={{ color: "#7B8B6F" }}>+{r.carico}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--ds-text-secondary)" }}>Uscite</div>
                          <div className="font-medium" style={{ color: "#8B3A36" }}>−{r.scarico}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--ds-text-secondary)" }}>Netto</div>
                          <div className="font-medium">{r.net >= 0 ? `+${r.net}` : r.net}</div>
                        </div>
                      </div>
                      <div className="text-[11px] mt-2" style={{ color: "var(--ds-text-secondary)" }}>{r.count} movimenti</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Dettaglio cronologico */}
            <div className="mt-6 border-t pt-4" style={{ borderColor: "var(--ds-border)" }}>
              <div className="label-tag mb-3">Cronologia dettagliata ({movements.length})</div>
              <div className="overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0" style={{ background: "var(--ds-surface)" }}>
                    <tr style={{ borderBottom: "1px solid var(--ds-border)" }}>
                      <th className="text-left py-2 label-tag">Data</th>
                      <th className="text-left py-2 label-tag">Tipo</th>
                      <th className="text-left py-2 label-tag">Deposito</th>
                      <th className="text-right py-2 label-tag">Q.tà</th>
                      <th className="text-left py-2 label-tag pl-3">Utente</th>
                      <th className="text-left py-2 label-tag pl-3">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.map((m) => {
                      const depName = deposits.find((d) => d.id === (m.deposito_id || m.to_deposito_id))?.nome || "—";
                      const fromName = m.from_deposito_id ? deposits.find((d) => d.id === m.from_deposito_id)?.nome : null;
                      const isCarico = m.tipo === "carico";
                      return (
                        <tr key={m.id} style={{ borderBottom: "1px solid var(--ds-border)" }}>
                          <td className="py-2 text-xs">{new Date(m.created_at).toLocaleString("it-IT")}</td>
                          <td className="py-2">
                            <span className="text-xs font-medium" style={{ color: isCarico ? "#7B8B6F" : "#8B3A36" }}>
                              {isCarico ? "carico" : "scarico"}
                            </span>
                          </td>
                          <td className="py-2 text-xs">
                            {fromName ? `${fromName} → ${depName}` : depName}
                          </td>
                          <td className="py-2 text-right font-medium">
                            <span style={{ color: isCarico ? "#7B8B6F" : "#8B3A36" }}>
                              {isCarico ? "+" : "−"}{m.quantita}
                            </span>
                          </td>
                          <td className="py-2 text-xs pl-3">{m.utente_nome || "—"}</td>
                          <td className="py-2 text-xs pl-3" style={{ color: "var(--ds-text-secondary)" }}>{m.note || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Dialog */}
      {form && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dettaglio-edit-dialog">
            <DialogHeader>
              <DialogTitle className="font-brand text-3xl font-light">Modifica prodotto</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-6 pt-2">
              <div>
                <div className="label-tag mb-3">Anagrafica</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label className="label-tag">Nome articolo *</Label>
                    <Input value={form.nome_articolo} onChange={(e) => updateForm({ nome_articolo: e.target.value })} className="mt-1.5 rounded-lg" required />
                  </div>
                  <div>
                    <Label className="label-tag flex items-center gap-1"><Scan size={12} /> Codice a barre</Label>
                    <Input value={form.codice_barre || ""} onChange={(e) => updateForm({ codice_barre: e.target.value })} className="mt-1.5 rounded-lg font-mono" />
                  </div>
                  <div>
                    <Label className="label-tag">Codice articolo fornitore</Label>
                    <Input
                      value={form.codice_articolo_fornitore || ""}
                      onChange={(e) => updateForm({ codice_articolo_fornitore: e.target.value })}
                      placeholder="Es. SKU fornitore"
                      className="mt-1.5 rounded-lg font-mono"
                      data-testid="dettaglio-codice-articolo-fornitore"
                    />
                  </div>
                  <div>
                    <Label className="label-tag">Fornitore</Label>
                    <Select value={form.fornitore_id || "none"} onValueChange={(v) => {
                      if (v === "__add__") { setQuickSupplierOpen(true); return; }
                      updateForm({ fornitore_id: v === "none" ? "" : v });
                    }}>
                      <SelectTrigger className="mt-1.5 rounded-lg" data-testid="dettaglio-fornitore"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nessuno</SelectItem>
                        {suppliersSorted.map((s) => <SelectItem key={s.id} value={s.id}>{s.ragione_sociale}</SelectItem>)}
                        <SelectSeparator />
                        <SelectItem value="__add__" data-testid="dettaglio-add-fornitore-option">
                          <span className="inline-flex items-center gap-1.5" style={{ color: "var(--ds-primary)" }}>
                            <Plus size={12} /> Aggiungi fornitore
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-tag">Categoria *</Label>
                    <Select value={form.categoria_id} onValueChange={(v) => updateForm({ categoria_id: v })}>
                      <SelectTrigger className="mt-1.5 rounded-lg"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-tag">Deposito *</Label>
                    <Select value={form.deposito_id} onValueChange={(v) => updateForm({ deposito_id: v })}>
                      <SelectTrigger className="mt-1.5 rounded-lg"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                      <SelectContent>
                        {depositsSorted.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome} {d.preferito ? "★" : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <div className="label-tag mb-3">Unità di magazzino & quantità</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="label-tag">Quantità *</Label>
                    <Input type="number" step="any" value={numVal(form.quantita)}
                      onChange={(e) => updateForm({ quantita: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                      className="mt-1.5 rounded-lg" required />
                  </div>
                  <div>
                    <Label className="label-tag">Soglia minima</Label>
                    <Input type="number" step="any" value={numVal(form.soglia_minima)}
                      onChange={(e) => updateForm({ soglia_minima: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                      className="mt-1.5 rounded-lg" />
                  </div>
                  <div>
                    <Label className="label-tag">Unità di misura base *</Label>
                    <Select value={form.unita_misura_id} onValueChange={(v) => updateForm({ unita_misura_id: v })}>
                      <SelectTrigger className="mt-1.5 rounded-lg" data-testid="dettaglio-unita"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                      <SelectContent>
                        {baseUnits.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="text-[11px] mt-1" style={{ color: "var(--ds-text-secondary)" }}>
                      Es. pezzo, kg — è l'unità con cui il prodotto vive a magazzino. La confezione non è ammessa qui.
                    </div>
                  </div>
                </div>
              </div>

              {/* Prodotti per ordini — moltiplicatore confezione (opzionale) */}
              <div>
                <div className="label-tag mb-3">Prodotti per ordini</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="label-tag">Pezzi per confezione</Label>
                    <Input
                      type="number"
                      min="0"
                      value={numVal(form.pezzi_per_confezione)}
                      onChange={(e) => updateForm({ pezzi_per_confezione: e.target.value === "" ? "" : parseInt(e.target.value) })}
                      placeholder="es. 24"
                      className="mt-1.5 rounded-lg"
                      data-testid="dettaglio-pezzi"
                    />
                    <div className="text-[11px] mt-1" style={{ color: "var(--ds-text-secondary)" }}>
                      Opzionale — quanti pezzi (o kg) contiene una confezione. Se lasciato vuoto, il prodotto <b>non</b> potrà essere ordinato a confezioni.
                      L'ordine a confezioni è riservato agli amministratori.
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="label-tag mb-3">Prezzo di acquisto</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="label-tag">Senza IVA (€) *</Label>
                    <Input type="number" step="0.01" value={numVal(form.prezzo_acquisto)} onChange={(e) => onPriceField("prezzo_acquisto", e.target.value)} className="mt-1.5 rounded-lg" required />
                  </div>
                  <div>
                    <Label className="label-tag">IVA (%) *</Label>
                    <Input type="number" step="0.01" value={numVal(form.iva_acquisto)} onChange={(e) => onPriceField("iva_acquisto", e.target.value)} className="mt-1.5 rounded-lg" required />
                  </div>
                  <div>
                    <Label className="label-tag">Con IVA (€)</Label>
                    <Input type="number" step="0.01" value={numVal(form.prezzo_acquisto_con_iva)} onChange={(e) => onPriceField("prezzo_acquisto_con_iva", e.target.value)} className="mt-1.5 rounded-lg" />
                  </div>
                </div>
              </div>

              <div>
                <div className="label-tag mb-3">Prezzo di vendita</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="label-tag">Senza IVA (€)</Label>
                    <Input type="number" step="0.01" value={numVal(form.prezzo_vendita)} onChange={(e) => onPriceField("prezzo_vendita", e.target.value)} className="mt-1.5 rounded-lg" />
                  </div>
                  <div>
                    <Label className="label-tag">IVA (%)</Label>
                    <Input type="number" step="0.01" value={numVal(form.iva_vendita)} onChange={(e) => onPriceField("iva_vendita", e.target.value)} className="mt-1.5 rounded-lg" />
                  </div>
                  <div>
                    <Label className="label-tag">Con IVA (€)</Label>
                    <Input type="number" step="0.01" value={numVal(form.prezzo_vendita_con_iva)} onChange={(e) => onPriceField("prezzo_vendita_con_iva", e.target.value)} className="mt-1.5 rounded-lg" />
                  </div>
                  <div>
                    <Label className="label-tag">Ricarico (%)</Label>
                    <Input type="number" step="0.01" value={numVal(form.ricarico)} readOnly className="mt-1.5 rounded-lg bg-[var(--ds-bg)]" />
                  </div>
                  <div>
                    <Label className="label-tag">Minimo d'ordine</Label>
                    <Input type="number" step="any" value={numVal(form.minimo_ordine)}
                      onChange={(e) => updateForm({ minimo_ordine: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                      className="mt-1.5 rounded-lg" />
                  </div>
                </div>
              </div>

              <div>
                <Label className="label-tag">Nota</Label>
                <Textarea value={form.nota || ""} onChange={(e) => updateForm({ nota: e.target.value })} className="mt-1.5 rounded-lg" rows={2} />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
                <Button type="submit" className="ds-btn-primary" style={{ background: "var(--ds-primary)" }} data-testid="dettaglio-submit">
                  Salva modifiche
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il prodotto?</AlertDialogTitle>
            <AlertDialogDescription>Verranno rimossi anche i movimenti e lo storico prezzi associati.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} style={{ background: "var(--ds-danger)" }} data-testid="dettaglio-confirm-delete">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick-add supplier */}
      <Dialog open={quickSupplierOpen} onOpenChange={setQuickSupplierOpen}>
        <DialogContent data-testid="quick-fornitore-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-2xl font-light">Aggiungi fornitore rapido</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitQuickSupplier} className="space-y-3 pt-2">
            <div>
              <Label className="label-tag">Ragione sociale *</Label>
              <Input value={quickSupplier.ragione_sociale} onChange={(e) => setQuickSupplier({ ...quickSupplier, ragione_sociale: e.target.value })} className="mt-1.5 rounded-lg" required data-testid="quick-forn-ragione" />
            </div>
            <div>
              <Label className="label-tag">Nome referente *</Label>
              <Input value={quickSupplier.nome} onChange={(e) => setQuickSupplier({ ...quickSupplier, nome: e.target.value })} className="mt-1.5 rounded-lg" required data-testid="quick-forn-nome" />
            </div>
            <div>
              <Label className="label-tag">Email *</Label>
              <Input type="email" value={quickSupplier.email} onChange={(e) => setQuickSupplier({ ...quickSupplier, email: e.target.value })} className="mt-1.5 rounded-lg" required data-testid="quick-forn-email" />
            </div>
            <div>
              <Label className="label-tag">Telefono *</Label>
              <Input value={quickSupplier.telefono} onChange={(e) => setQuickSupplier({ ...quickSupplier, telefono: e.target.value })} className="mt-1.5 rounded-lg" required data-testid="quick-forn-telefono" />
            </div>
            <div className="text-xs" style={{ color: "var(--ds-text-secondary)" }}>
              I dettagli completi (P.IVA, indirizzo…) si aggiungono dalla pagina Fornitori.
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setQuickSupplierOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={quickBusy} className="ds-btn-primary" style={{ background: "var(--ds-primary)" }} data-testid="quick-forn-submit">
                {quickBusy ? "Salvataggio…" : "Crea e seleziona"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
